// filepath: /Users/raminadam/projects/Maki-gewinnspiel/MakiAndTea/frontend/src/content/pages/external/PrivacyPage.tsx
import { type ReactElement, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import axios from 'axios';
import config from 'src/config';
import { useAuth } from 'src/util/authenticationProvider';

const defaultHtml = `
  <div style="padding: 2rem; max-width: 800px; margin: 0 auto;">
    <h1>Datenschutzerklärung für das Gewinnspiel-System</h1>

    <p><strong>Verantwortlich für die Datenverarbeitung:</strong></p>
    <p>
      Maki &amp; Tea<br />
      Mülheimer Str. 20<br />
      46049 Oberhausen<br />
      Inhaber: Mert Gülcan<br />
      E-Mail: <a href="mailto:Info@MakiAndTea.de">Info@MakiAndTea.de</a>
    </p>

    <h2>1. Zweck der Verarbeitung</h2>
    <p>
      Wir verarbeiten Ihre personenbezogenen Daten ausschließlich zur
      Durchführung unserer Gewinnspiele. Dazu gehören insbesondere:
    </p>
    <ul>
      <li>Durchführung der Auslosung und Benachrichtigung der Gewinner</li>
      <li>Verwaltung und Versand von Gewinnen</li>
      <li>Sicherstellung eines fairen Ablaufs (z. B. Verhinderung von Mehrfachteilnahmen)</li>
    </ul>

    <h2>2. Erhobene Daten</h2>
    <p>Im Rahmen der Teilnahme an unseren Gewinnspielen können wir folgende Daten verarbeiten:</p>
    <ul>
      <li>Name und ggf. Vorname</li>
      <li>E-Mail-Adresse</li>
      <li>Telefonnummer (falls angegeben)</li>
      <li>Teilnahmeinformationen (z. B. Datum der Teilnahme, Lucky Numbers)</li>
      <li>Informationen zur Gewinnannahme und Kontaktstatus</li>
    </ul>

    <h2>3. Rechtsgrundlage</h2>
    <p>
      Die Verarbeitung erfolgt auf Grundlage von <strong>Art. 6 Abs. 1 lit. b DSGVO</strong> (Vertragserfüllung im Rahmen des Gewinnspiels)
      sowie ggf. <strong>Art. 6 Abs. 1 lit. f DSGVO</strong> (berechtigtes Interesse an einer fairen Durchführung).
    </p>

    <h2>4. Speicherdauer</h2>
    <p>
      Wir speichern Ihre personenbezogenen Daten nur so lange, wie sie für die Durchführung und Abwicklung des
      Gewinnspiels erforderlich sind. Nach Beendigung und Übergabe der Gewinne werden Ihre Daten spätestens nach
      <strong> 6 Monaten</strong> gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten bestehen.
    </p>

    <h2>5. Weitergabe an Dritte</h2>
    <p>
      Ihre Daten werden nicht an Dritte weitergegeben, außer wenn dies für die Durchführung des Gewinnspiels notwendig
      ist (z. B. Versanddienstleister) oder wir gesetzlich dazu verpflichtet sind.
    </p>

    <h2>6. Ihre Rechte</h2>
    <p>
      Sie haben das Recht, jederzeit Auskunft über Ihre gespeicherten Daten zu erhalten sowie deren Berichtigung,
      Löschung oder Einschränkung der Verarbeitung zu verlangen. Außerdem können Sie der Verarbeitung widersprechen und
      haben das Recht auf Datenübertragbarkeit.
    </p>
    <p>
      Wenden Sie sich hierzu bitte an: <a href="mailto:Info@MakiAndTea.de">Info@MakiAndTea.de</a>
    </p>
    <p>
      Zudem haben Sie das Recht, sich bei einer <strong>Datenschutzaufsichtsbehörde</strong> zu beschweren.
    </p>

    <h2>7. Sicherheit</h2>
    <p>
      Wir setzen technische und organisatorische Maßnahmen ein, um Ihre Daten vor Verlust, Manipulation und unberechtigtem Zugriff zu
      schützen. Diese Maßnahmen werden regelmäßig überprüft und dem Stand der Technik angepasst.
    </p>
  </div>
`;

type LegalItem = {
  id?: string;
  title?: string;
  category?: string;
  content?: string;
};

export default function PrivacyPage(): ReactElement {
  const auth = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [html, setHtml] = useState<string>(defaultHtml);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // request the specific category endpoint
        const res = await axios.get(`${config.apiUrl}/api/legal/datenschutz`, {
          headers: auth?.tokens?.accessToken ? { Authorization: `Bearer ${auth.tokens.accessToken}` } : undefined,
          timeout: 7000
        });

        if (!mounted) return;

        // Backend wraps responses in Message<T> => data is under res.data.data
        const message = res?.data;
        const payload = message?.data ?? message;

        let items: LegalItem[] = [];

        if (!payload) {
          items = [];
        } else if (Array.isArray(payload)) {
          items = payload as LegalItem[];
        } else if (typeof payload === 'object') {
          if (Array.isArray((payload as any).content)) {
            items = (payload as any).content;
          } else if ((payload as any).id || (payload as any).content) {
            items = [payload as LegalItem];
          } else {
            items = [];
          }
        } else {
          items = [];
        }

        const first = items[0];
        if (first && first.content) {
          setHtml(String(first.content));
        } else {
          setHtml(defaultHtml);
        }
      } catch (e) {
        setHtml(defaultHtml);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [auth?.tokens]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <Box
        component="article"
        sx={{
          width: { xs: '95%', md: '80%' },
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
          p: { xs: 2, md: 4 },
          overflow: 'hidden',
          '& img': { maxWidth: '100%', height: 'auto' }
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </Box>
    </Box>
  );
}
